import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { client } from "@/lib/api";
import { VERSE_THEMES, VERSE_LIBRARY } from "@/data/verses";
import { BookOpenText, MessageCircleHeart, Pin, Quote } from "lucide-react";

export default function VersesPage() {
  const navigate = useNavigate();
  const [theme, setTheme] = useState(VERSE_THEMES[0].key);
  const [pinning, setPinning] = useState(null);
  const verses = VERSE_LIBRARY[theme] || [];

  const sendToCompanion = (v) => {
    sessionStorage.setItem(
      "companion_seed",
      `Help me reflect on this verse: "${v.text}" (${v.ref}). What might God be saying to me here as I fight this craving?`
    );
    navigate("/app/coach");
  };

  const setAsToday = async (v) => {
    setPinning(v.ref);
    try {
      await client.post("/prefs/verse", { text: v.text, ref: v.ref });
      toast.success("Set as your verse for Today. It'll greet you on your home screen.");
    } catch (e) {
      toast.error("Couldn't set that verse. Please try again.");
    } finally {
      setPinning(null);
    }
  };

  return (
    <div className="space-y-7" data-testid="verses-page">
      <div>
        <div className="flex items-center gap-2 mb-2">
          <BookOpenText className="w-5 h-5 text-terracotta" />
          <p className="uppercase tracking-[0.2em] text-xs text-terracotta">Verse library</p>
        </div>
        <h1 className="font-serif text-display text-olive">Words for every moment</h1>
        <p className="text-olive/70 mt-3 max-w-2xl">
          Pick what you're carrying and find a verse to hold onto. Send it to your Companion to pray it through,
          or pin it to your home screen for the day.
        </p>
      </div>

      <div className="flex flex-wrap gap-2" data-testid="verse-themes">
        {VERSE_THEMES.map((t) => {
          const active = t.key === theme;
          return (
            <button key={t.key} data-testid={`verse-theme-${t.key}`} onClick={() => setTheme(t.key)}
              className={`rounded-full px-4 py-2 text-sm font-medium border transition-colors duration-200 ${active ? "bg-olive text-sand border-olive" : "bg-card text-olive/70 border-border hover:bg-sage"}`}>
              {t.label}
            </button>
          );
        })}
      </div>

      <div className="grid md:grid-cols-2 gap-4" data-testid="verse-list">
        {verses.map((v) => (
          <div key={v.ref} data-testid={`verse-${v.ref.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
            className="relative overflow-hidden rounded-3xl bg-card border border-border p-6 shadow-soft flex flex-col">
            <Quote className="absolute -top-2 -left-1 w-16 h-16 text-sage" />
            <p className="font-scripture italic text-scripture text-olive leading-relaxed relative z-10">“{v.text}”</p>
            <p className="text-sm text-stone mt-3 mb-5">{v.ref}</p>
            <div className="mt-auto flex flex-col sm:flex-row gap-2.5">
              <button data-testid={`verse-send-${v.ref.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`} onClick={() => sendToCompanion(v)}
                className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-terracotta text-sand py-2.5 text-sm font-medium transition-colors hover:bg-terracotta-soft">
                <MessageCircleHeart className="w-4 h-4" /> Send to Companion
              </button>
              <button data-testid={`verse-pin-${v.ref.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`} onClick={() => setAsToday(v)} disabled={pinning === v.ref}
                className="flex-1 inline-flex items-center justify-center gap-2 rounded-full border border-border text-olive py-2.5 text-sm font-medium transition-colors hover:bg-sage">
                <Pin className="w-4 h-4" /> {pinning === v.ref ? "Pinning…" : "Set as Today verse"}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
